'use client';

import { useQuery } from '@apollo/client/react';
import {
  faAddressBook,
  faAddressCard,
  faBagsShopping,
  faClone,
  faCog,
  faEnvelope,
  faExclamationTriangle,
  faHomeAlt,
  faImages,
  faMegaphone,
  faSparkles,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider, Stack } from '@mantine/core';
import { analytics } from '@modules/analytics';
import { useFeatureFlag } from '@modules/feature-flag';
import { IntegrationsPageDataDocument } from 'app/[workspace]/workspace-settings/integrations/_components/integrations-page-data.graphql';
import { MergeLinkedAccountsForIntegrationsDocument } from 'app/[workspace]/workspace-settings/integrations/_components/merge-linked-accounts.graphql';
import { getUpgradeableIntegrations } from 'lib/crm/get-upgradeable-integrations';
import {
  CUSTOM_LEAD_FORM_EDUCATION,
  LIST_ENRICHMENT_DISCOVERY,
} from 'modules/global-modals/modals/feature-discovery/constants';
import { useMemo } from 'react';
import { palette } from 'ui-lib/src/lib/color/palette';

import { WORKSPACE_ROUTES } from '../../constants';
import { isEditingCard, isEditingTemplate } from '../../identity-editor-active';
import type { WorkspaceRole } from '../../types';
import { NavLink } from '../NavLink/NavLink';
import { FlaggedNavLink } from './FlaggedNavLink';
import { navLinkListStyle } from './styles.css';

export function TeamWorkspaceNavLinks({
  workspaceId,
  workspaceRole,
  showDashHome,
}: {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
  /** Whether members get a Home link to the /dash home (phase-2 rollout). */
  showDashHome?: boolean;
}) {
  const [customSharebackFormEnabled] = useFeatureFlag('dash_custom_shareback_form', 'off');
  const [hubspotV2Flag] = useFeatureFlag('hubspot-v2-enabled', 'off');
  const [salesforceV2Flag] = useFeatureFlag('salesforce-v2-enabled', 'off');
  const [msDynamicsSalesV2Flag] = useFeatureFlag('ms-dynamics-sales-v2-enabled', 'off');
  const [zohoV2Flag] = useFeatureFlag('zoho-v2-native', 'off');
  const [pipedriveV2Flag] = useFeatureFlag('pipedrive-native-enabled', 'off');

  const { data: integrationsData } = useQuery(IntegrationsPageDataDocument, {
    variables: { workspaceId, workspaceIdString: workspaceId },
    fetchPolicy: 'cache-first',
  });

  const mergeEnabled = integrationsData?.workspace?.tags?.enterprise_integrations_enabled ?? false;

  const { data: mergeData } = useQuery(MergeLinkedAccountsForIntegrationsDocument, {
    variables: { workspaceId },
    fetchPolicy: 'cache-first',
    skip: !mergeEnabled,
  });

  const hasUpgradeableIntegrations = useMemo(() => {
    const mergeAccounts = (mergeData?.getMergeLinkedAccounts ?? []).map((m) => ({
      service: m.integration,
      source: 'merge' as const,
    }));
    const allIntegrations = [...(integrationsData?.crmIntegrations ?? []), ...mergeAccounts];
    return (
      getUpgradeableIntegrations(allIntegrations, {
        canUseHubspotV2: hubspotV2Flag === 'on',
        canUseSalesforceV2: salesforceV2Flag === 'on',
        canUseMsDynamicsSalesV2: msDynamicsSalesV2Flag === 'on',
        canUseZohoV2: zohoV2Flag === 'on',
        canUsePipedriveV2: pipedriveV2Flag === 'on',
      }).length > 0
    );
  }, [
    integrationsData,
    mergeData,
    hubspotV2Flag,
    salesforceV2Flag,
    msDynamicsSalesV2Flag,
    zohoV2Flag,
    pipedriveV2Flag,
  ]);

  const handleCampaignsClick = () => {
    analytics.track({
      name: 'navigation_item_clicked',
      properties: {
        item: 'campaigns',
        navigation_type: 'sidebar',
        workspace_id: workspaceId,
      },
    });
  };

  const handleListEnrichmentClick = () => {
    analytics.track({
      name: 'navigation_item_clicked',
      properties: {
        item: 'list_enrichment',
        navigation_type: 'sidebar',
        workspace_id: workspaceId,
      },
    });
  };

  const handleSettingsClick = () => {
    analytics.track({
      name: 'navigation_item_clicked',
      properties: {
        item: 'settings',
        navigation_type: 'sidebar',
        workspace_id: workspaceId,
        has_upgradeable_integrations: hasUpgradeableIntegrations,
      },
    });
  };

  return (
    <ul className={navLinkListStyle}>
      {/* Admins get the admin dashboard as Home; members get the /dash home
        when the phase-2 rollout enables it for them. */}
      {workspaceRole !== 'MEMBER' ? (
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faHomeAlt} />}
          label="Home"
          href={`/${workspaceId}${WORKSPACE_ROUTES.HOME}`}
          prefetch={true}
        />
      ) : showDashHome ? (
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faHomeAlt} />}
          label="Home"
          href={`/${workspaceId}${WORKSPACE_ROUTES.DASH}`}
          prefetch={true}
        />
      ) : null}
      <Divider />
      <Stack gap="xsmall">
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faUser} />}
          label="Team Members"
          href={`/${workspaceId}${WORKSPACE_ROUTES.TEAM_MEMBERS}`}
          prefetch={true}
        />
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faClone} />}
          label="Team Templates"
          href={`/${workspaceId}${WORKSPACE_ROUTES.TEMPLATES}`}
          isActiveOverride={isEditingTemplate(workspaceId)}
          featureDiscoveryKey={
            customSharebackFormEnabled === 'on' ? CUSTOM_LEAD_FORM_EDUCATION : undefined
          }
          prefetch={true}
        />
      </Stack>
      <Divider />
      <Stack gap="xsmall">
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faAddressBook} />}
          label="Contacts"
          href={`/${workspaceId}${WORKSPACE_ROUTES.CONTACTS}`}
          prefetch={true}
        />
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faMegaphone} />}
          label="Campaigns"
          href={`/${workspaceId}${WORKSPACE_ROUTES.CAMPAIGNS}`}
          onClick={handleCampaignsClick}
          featureDiscoveryKey="CAMPAIGNS"
          prefetch={true}
        />
        <FlaggedNavLink
          workspaceId={workspaceId}
          flagName="list-enrichment-v2"
          flagFallback="off"
          flagActiveValue="on"
          icon={faSparkles}
          label="Bulk Enrichment"
          href={WORKSPACE_ROUTES.LIST_ENRICHMENT}
          onClick={handleListEnrichmentClick}
          featureDiscoveryKey={LIST_ENRICHMENT_DISCOVERY}
        />
      </Stack>
      <Divider />
      <Stack gap="xsmall">
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faAddressCard} />}
          label="Cards"
          href={`/${workspaceId}${WORKSPACE_ROUTES.TEAM_CARDS}`}
          isActiveOverride={isEditingCard(workspaceId)}
          prefetch={true}
        />
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faEnvelope} />}
          label="Email Signatures"
          href={`/${workspaceId}${WORKSPACE_ROUTES.EMAIL_SIGNATURES}`}
          prefetch={true}
        />
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faImages} />}
          label="Virtual Backgrounds"
          href={`/${workspaceId}${WORKSPACE_ROUTES.VIRTUAL_BACKGROUNDS}`}
        />
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faBagsShopping} />}
          label="Accessories"
          href={`/${workspaceId}${WORKSPACE_ROUTES.ACCESSORIES}`}
        />
      </Stack>
      <Divider />
      <Stack gap="xsmall">
        <NavLink
          leadingIcon={<FontAwesomeIcon icon={faCog} />}
          label="Settings"
          href={`/${workspaceId}${WORKSPACE_ROUTES.WORKSPACE_SETTINGS}`}
          onClick={handleSettingsClick}
          trailingIndicator={
            hasUpgradeableIntegrations ? (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                color={palette.yellow[100]}
                size="sm"
                style={{ marginLeft: 'auto', flexShrink: 0 }}
              />
            ) : undefined
          }
          prefetch={true}
        />
      </Stack>
    </ul>
  );
}

export function PersonalWorkspaceNavLinks({
  workspaceId,
  showDashHome,
}: {
  workspaceId: string;
  showDashHome?: boolean;
}) {
  return (
    <ul className={navLinkListStyle}>
      {showDashHome && (
        <>
          <NavLink
            leadingIcon={<FontAwesomeIcon icon={faHomeAlt} />}
            label="Home"
            href={`/${workspaceId}${WORKSPACE_ROUTES.DASH}`}
          />
          <Divider />
        </>
      )}
      <NavLink
        leadingIcon={<FontAwesomeIcon icon={faAddressBook} />}
        label="Contacts"
        href={`/${workspaceId}${WORKSPACE_ROUTES.CONTACTS}`}
        prefetch={true}
      />
      <Divider />

      <NavLink
        leadingIcon={<FontAwesomeIcon icon={faAddressCard} />}
        label="Cards"
        href={`/${workspaceId}${WORKSPACE_ROUTES.PERSONAL_CARDS}`}
        isActiveOverride={isEditingCard(workspaceId)}
        prefetch={true}
      />
      <NavLink
        leadingIcon={<FontAwesomeIcon icon={faEnvelope} />}
        label="Email Signatures"
        href={`/${workspaceId}${WORKSPACE_ROUTES.EMAIL_SIGNATURES}`}
        prefetch={true}
      />
      <NavLink
        leadingIcon={<FontAwesomeIcon icon={faImages} />}
        label="Virtual Backgrounds"
        href={`/${workspaceId}${WORKSPACE_ROUTES.VIRTUAL_BACKGROUNDS}`}
        prefetch={true}
      />
      <NavLink
        leadingIcon={<FontAwesomeIcon icon={faBagsShopping} />}
        label="Accessories"
        href={`/${workspaceId}${WORKSPACE_ROUTES.ACCESSORIES}`}
        prefetch={true}
      />
    </ul>
  );
}
